/* eslint-disable react/no-array-index-key */

'use client'

import { serializeJsonLd } from 'lib/jsonLd'
import React, { useState, useEffect } from 'react'
import { Accordion, Col, Container, Row } from 'react-bootstrap'
import styles from 'styles/ai.module.css'
import { WordFill } from 'hooks/WordFill'

export interface FaqItem {
  question: JSX.Element | string
  answer: JSX.Element | string
}

interface FaqAiPageProps {
  items: FaqItem[]
  title?: JSX.Element | string
  titleorange?: JSX.Element | string
  subtitle?: JSX.Element | string
  subtitlegrey?: JSX.Element | string
  paddingClass?: string
  className?: string
  titleClassName?: string
  subtitleClassName?: string
}

const FaqAiPage: React.FC<FaqAiPageProps> = ({
  items,
  titleorange,
  title,
  subtitle,
  subtitlegrey,
  paddingClass,
  className,
  titleClassName,
  subtitleClassName,
}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  // Handle subtitle properly - use WordFill for strings, custom processing for JSX
  const renderSubtitle = () => {
    if (subtitle && typeof subtitle === 'string') {
      return <WordFill text={`${subtitle}${subtitlegrey || ''}`} />
    }

    // For JSX elements, we need to process them to preserve <br> tags while applying WordFill
    if (subtitle) {
      // Process JSX children to apply WordFill to text segments
      const processChildren = (children: any): any => {
        if (Array.isArray(children)) {
          return children.map((child, index) => {
            if (typeof child === 'string') {
              return <WordFill key={index} text={child} />
            }
            if (child && typeof child === 'object' && child.props) {
              return {
                ...child,
                props: {
                  ...child.props,
                  children: processChildren(child.props.children),
                },
              }
            }
            return child
          })
        }
        if (typeof children === 'string') {
          return <WordFill text={children} />
        }
        return children
      }

      // Process the subtitle JSX
      if (subtitle && typeof subtitle === 'object' && subtitle.props) {
        return {
          ...subtitle,
          props: {
            ...subtitle.props,
            children: processChildren(subtitle.props.children),
          },
        }
      }

      return subtitle
    }
    return null
  }

  // Check if device is mobile and handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    // Check on mount
    checkMobile()

    // Add event listener for window resize
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // Set initial state based on device type
  useEffect(() => {
    if (isMobile) {
      // On mobile, keep all items collapsed
      setActiveKey(null)
    } else {
      // On desktop, expand first item by default
      setActiveKey('0')
    }
  }, [isMobile])

  const handleToggle = (key: string) => {
    if (isMobile) {
      // On mobile, allow toggling but keep only one item open at a time
      setActiveKey((prev) => (prev === key ? null : key))
    } else {
      // On desktop, allow multiple items to be open
      setActiveKey((prev) => (prev === key ? null : key))
    }
  }

  // Helper function to extract text from JSX or string
  const extractText = (content: JSX.Element | string): string => {
    if (typeof content === 'string') {
      return content
    }
    if (typeof content === 'object' && content !== null && 'props' in content) {
      // Extract text from JSX element recursively
      if (typeof content.props.children === 'string') {
        return content.props.children
      }
      if (Array.isArray(content.props.children)) {
        return content.props.children
          .map((child: any) => {
            if (typeof child === 'string') {
              return child
            }
            if (child && typeof child === 'object' && child.props) {
              return extractText(child)
            }
            return ''
          })
          .join(' ')
      }
      if (content.props.children) {
        return extractText(content.props.children)
      }
    }
    return ''
  }

  // Generate FAQ schema from items prop
  const schemaDataFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: extractText(item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: extractText(item.answer),
      },
    })),
  }
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schemaDataFAQ) }}
      />

      <section
        className={`${styles.faq_ai_page} ${styles.commonPad} faq-ai-page ${
          className || ''
        }`}
      >
        <Container>
          <Row className="mb-4 pb-lg-4 align-items-center justify-content-between">
            {/* Without a subtitle the title spans the row, centered on one line */}
            <Col
              md={subtitle ? 5 : 12}
              className={subtitle ? '' : 'text-center'}
            >
              {title && (
                <h3
                  className={`section-title text-black mb-sm-3 mb-2 mb-lg-0 ${
                    titleClassName || ''
                  }`}
                >
                  {title}
                  {/* Separator only when there is an orange part to follow */}
                  {titleorange && (subtitle ? <br /> : ' ')}
                  {titleorange && (
                    <span className="text-orange-new">{titleorange}</span>
                  )}
                </h3>
              )}
            </Col>
            {subtitle && (
              <Col md={5}>
                <div className="d-flex flex-column align-items-start align-items-lg-end">
                  <p
                    className={`section-desc sec-subtitle mb-0 ${
                      paddingClass || ''
                    } ${subtitleClassName || ''}`}
                  >
                    {renderSubtitle()}
                  </p>
                </div>
              </Col>
            )}
          </Row>
          <Accordion activeKey={activeKey ?? ''} alwaysOpen={!isMobile}>
            {items.map((item, index) => {
              const key = index.toString()
              const isActive = activeKey === key
              return (
                <Accordion.Item
                  eventKey={key}
                  key={index}
                  className={isActive ? 'open-item' : ''}
                >
                  <Accordion.Header
                    className={`${styles.faq_que} text-black`}
                    onClick={() => handleToggle(key)}
                  >
                    {item.question}
                  </Accordion.Header>
                  <Accordion.Body className={`${styles.faq_ans} pt-1`}>
                    {item.answer}
                  </Accordion.Body>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </Container>
      </section>
    </>
  )
}

export default FaqAiPage
